import React, { useState, useEffect, useCallback } from "react";
import VisierRunbook from "./VisierRunbook";

const API_BASE = process.env.REACT_APP_API_BASE || "https://your-api-gateway.execute-api.us-east-1.amazonaws.com/prod";

async function apiFetch(path) {
  const sep = path.includes("?") ? "&" : "?";
  const res = await fetch(`${API_BASE}${path}${sep}_=${Date.now()}`);
  if (!res.ok) { const t = await res.text(); throw new Error(`API error ${res.status}: ${t}`); }
  return res.json();
}

async function apiPost(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch {}
  if (!res.ok) {
    const error = new Error(
      data?.error?.localizedMessage ||
      data?.error?.message ||
      data?.message ||
      (typeof data?.error === "string" ? data.error : "") ||
      text ||
      `API error ${res.status}`
    );
    error.data = data;
    throw error;
  }
  return data;
}

async function apiPut(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch {}
  if (!res.ok) {
    const error = new Error(
      data?.error?.localizedMessage ||
      data?.error?.message ||
      data?.message ||
      (typeof data?.error === "string" ? data.error : "") ||
      text ||
      `API error ${res.status}`
    );
    error.data = data;
    throw error;
  }
  return data;
}

async function apiDelete(path, body) {
  const res = await fetch(`${API_BASE}${path}`, {
    method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
  });
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch {}
  if (!res.ok) {
    const error = new Error(
      data?.error?.localizedMessage ||
      data?.error?.message ||
      data?.message ||
      (typeof data?.error === "string" ? data.error : "") ||
      text ||
      `API error ${res.status}`
    );
    error.data = data;
    throw error;
  }
  return data;
}

function isTimeoutLikeError(err) {
  const msg = String(err?.message || err || "").toLowerCase();
  return msg.includes("endpoint request timed out") || msg.includes("api error 504") || msg.includes("504");
}

function permissionNames(permissions, ids) {
  return ids.map(id => permissions.find(p => p.id === id)?.displayName || id);
}

// ServiceNow incident created for the action (see Lambdas/visier_incident.py).
function IncidentNote({ incident }) {
  if (!incident) return null;
  if (incident.created) {
    return (
      <div style={{ marginTop: "6px", fontSize: "12px" }}>
        ServiceNow incident{" "}
        {incident.url
          ? <a href={incident.url} target="_blank" rel="noreferrer" style={{ color: "inherit", fontWeight: 700 }}>{incident.number}</a>
          : <b>{incident.number}</b>}
        {" "}created ({incident.state}) for KGS HRT Application Security
        {incident.environment === "dev" ? " in ServiceNow DEV" : ""}.
        {incident.warning && <span style={{ opacity: 0.8 }}> Note: {incident.warning}.</span>}
      </div>
    );
  }
  return (
    <div style={{ marginTop: "6px", fontSize: "12px", color: "#f0d48a" }}>
      ⚠ ServiceNow incident was not created{incident.environment === "dev" ? " (ServiceNow DEV)" : ""}: {incident.error || "unknown error"}.
      The Visier change is not affected; please log it manually.
    </div>
  );
}

// ── Result banner ──────────────────────────────────────────────────────────
function ResultBanner({ result, onDismiss }) {
  if (!result) return null;
  return (
    <div className={`cug-result ${result.success ? "cug-result-success" : "cug-result-error"}`}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div className="cug-result-title">{result.success ? "✓ Success" : "✗ Error"}</div>
          <div>{result.message}</div>
          {result.warningMessage && (
            <div style={{ marginTop: "6px", color: "var(--red)", fontWeight: 600 }}>
              {result.warningMessage}
            </div>
          )}
          <IncidentNote incident={result.incident || result.data?.serviceNowIncident} />
        </div>
        <button onClick={onDismiss} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer", fontSize: "16px", padding: "0 4px", opacity: 0.7 }}>×</button>
      </div>
      {result.data && !result.hideApiResponse && (
        <details style={{ marginTop: "8px" }}>
          <summary style={{ cursor: "pointer", fontSize: "12px" }}>View API response</summary>
          <pre className="cug-preview-pre" style={{ marginTop: "8px" }}>{JSON.stringify(result.data, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

// ── Multi-select dropdown ──────────────────────────────────────────────────
function MultiSelect({ label, items, selected, onToggle, loading, emptyLabel }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = items.filter(i => (i.displayName || i.id).toLowerCase().includes(search.toLowerCase()));
  const selectedItems = items.filter(i => selected.includes(i.id));

  return (
    <div className="cug-field">
      {label && (
        <label className="control-label">
          {label}
          {selected.length > 0 && <span className="cug-count-badge">{selected.length} selected</span>}
        </label>
      )}
      {selectedItems.length > 0 && (
        <div className="cug-tags">
          {selectedItems.map(item => (
            <span key={item.id} className="cug-tag">
              {item.displayName || item.id}
              <button className="cug-tag-remove" onClick={() => onToggle(item.id)}>×</button>
            </span>
          ))}
        </div>
      )}
      <div className={`cug-dropdown-trigger ${open ? "cug-dropdown-trigger-open" : ""}`}
        onClick={() => !loading && setOpen(o => !o)}>
        <span className="cug-dropdown-placeholder">{loading ? "Loading…" : emptyLabel}</span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && (
        <div className="cug-dropdown-panel">
          <div className="cug-dropdown-search-wrap">
            <input autoFocus className="cug-dropdown-search" placeholder="Search…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="cug-dropdown-list">
            {filtered.length === 0
              ? <div className="cug-dropdown-empty">No results</div>
              : filtered.map(item => {
                  const sel = selected.includes(item.id);
                  return (
                    <div key={item.id} className={`cug-dropdown-item ${sel ? "cug-dropdown-item-selected" : ""}`}
                      onClick={() => onToggle(item.id)}>
                      <span className={`cug-checkbox ${sel ? "cug-checkbox-checked" : ""}`}>
                        {sel && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5"><polyline points="2 6 5 9 10 3"/></svg>}
                      </span>
                      <span className="cug-dropdown-item-label">{item.displayName || item.id}</span>
                    </div>
                  );
                })}
          </div>
          <div className="cug-dropdown-footer">
            <button className="cug-done-btn" onClick={() => setOpen(false)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Single-select dropdown ─────────────────────────────────────────────────
function SingleSelect({ label, items, value, onChange, loading, placeholder }) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const selected = items.find(i => i.id === value);
  const filtered = items.filter(i => (i.displayName || i.id).toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="cug-field">
      {label && <label className="control-label">{label}</label>}
      <div className={`cug-dropdown-trigger ${open ? "cug-dropdown-trigger-open" : ""}`}
        onClick={() => !loading && setOpen(o => !o)}>
        <span className="cug-dropdown-placeholder" style={{ color: selected ? "var(--text-primary)" : undefined }}>
          {loading ? "Loading…" : selected ? selected.displayName : placeholder}
        </span>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          style={{ flexShrink: 0, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
          <polyline points="6 9 12 15 18 9"/>
        </svg>
      </div>
      {open && (
        <div className="cug-dropdown-panel">
          <div className="cug-dropdown-search-wrap">
            <input autoFocus className="cug-dropdown-search" placeholder="Search…"
              value={search} onChange={e => setSearch(e.target.value)} />
          </div>
          <div className="cug-dropdown-list">
            {filtered.length === 0
              ? <div className="cug-dropdown-empty">No results</div>
              : filtered.map(item => {
                  const sel = item.id === value;
                  return (
                    <div key={item.id} className={`cug-dropdown-item ${sel ? "cug-dropdown-item-selected" : ""}`}
                      onClick={() => { onChange(item.id); setOpen(false); setSearch(""); }}>
                      <span className={`cug-checkbox ${sel ? "cug-checkbox-checked" : ""}`}>
                        {sel && <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5"><polyline points="2 6 5 9 10 3"/></svg>}
                      </span>
                      <span className="cug-dropdown-item-label">{item.displayName || item.id}</span>
                    </div>
                  );
                })}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tab 1: Create Group ────────────────────────────────────────────────────
function CreateGroupTab({ permissions, loadingPerms }) {
  const [displayName, setDisplayName] = useState("");
  const [description, setDescription] = useState("");
  const [selPerms, setSelPerms]       = useState([]);
  const [submitting, setSubmitting]   = useState(false);
  const [result, setResult]           = useState(null);

  const togglePerm = useCallback(id =>
    setSelPerms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]), []);

  const handleReset = () => { setDisplayName(""); setDescription(""); setSelPerms([]); setResult(null); };

  const handleSubmit = async () => {
    if (!displayName.trim()) { setResult({ success: false, message: "Group name is required." }); return; }
    setSubmitting(true); setResult(null);
    try {
      const data = await apiPost("/user-groups", {
        displayName: displayName.trim(),
        description: description.trim(),
        permissionIds: selPerms,
        includeAllUsers: false,
        audit: { permissionNames: permissionNames(permissions, selPerms) },
      });
      const failures = data.failures || [];
      if (failures.length > 0) {
        setResult({ success: false, message: failures[0]?.message || "Visier rejected the request.", data });
      } else {
        setResult({ success: true, message: `User group "${displayName.trim()}" created successfully.`, data });
        setDisplayName(""); setDescription(""); setSelPerms([]);
      }
    } catch (err) {
      setResult({ success: false, message: err.message, incident: err.data?.serviceNowIncident });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />
      <div className="cug-field">
        <label className="control-label">Group Name <span style={{ color: "var(--red)" }}>*</span></label>
        <input className="cug-input" value={displayName} onChange={e => setDisplayName(e.target.value)}
          placeholder="e.g. HR Business Partners – APAC" />
      </div>
      <div className="cug-field">
        <label className="control-label">Description</label>
        <textarea className="cug-input cug-textarea" value={description}
          onChange={e => setDescription(e.target.value)}
          placeholder="Optional: describe the purpose of this user group" rows={3} />
      </div>
      <div className="cug-divider" />
      <MultiSelect
        label="Permissions"
        items={permissions}
        selected={selPerms}
        onToggle={togglePerm}
        loading={loadingPerms}
        emptyLabel={loadingPerms ? "Loading permissions…" : `Select permissions (${permissions.length} available)`}
      />
      <div className="cug-actions">
        <button className="compare-btn" onClick={handleSubmit} disabled={submitting || !displayName.trim()}>
          {submitting ? <><span className="spinner" /> Creating…</> : "Create User Group"}
        </button>
        <button className="cug-reset-btn" onClick={handleReset}>Reset</button>
      </div>
    </div>
  );
}

// ── Tab 2: Assign Permissions (additive) ───────────────────────────────────
function AssignPermissionsTab({ permissions, loadingPerms, userGroups, loadingGroups }) {
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selNewPerms, setSelNewPerms]     = useState([]);  // only NEW permissions to add
  const [submitting, setSubmitting]       = useState(false);
  const [result, setResult]               = useState(null);

  // Reset new selections when group changes
  useEffect(() => { setSelNewPerms([]); setResult(null); }, [selectedGroup]);

  const group          = userGroups.find(g => g.id === selectedGroup);
  const existingIds    = group?.permissionIds || [];

  // Dropdown only shows permissions the group does NOT already have
  const availablePerms = permissions.filter(p => !existingIds.includes(p.id));
  const existingPerms  = permissions.filter(p => existingIds.includes(p.id));

  const togglePerm = useCallback(id =>
    setSelNewPerms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]), []);

  const handleSubmit = async () => {
    if (!selectedGroup) { setResult({ success: false, message: "Please select a user group." }); return; }
    if (selNewPerms.length === 0) { setResult({ success: false, message: "Please select at least one permission to add." }); return; }
    setSubmitting(true); setResult(null);
    try {
      // Additive: existing + newly selected
      const mergedIds = [...new Set([...existingIds, ...selNewPerms])];
      const data = await apiPut("/user-groups/permissions", {
        userGroupId: selectedGroup,
        permissionIds: mergedIds,
        audit: {
          action: "assign",
          groupName: group?.displayName,
          addedPermissions: permissionNames(permissions, selNewPerms),
          resultingPermissionNames: permissionNames(permissions, mergedIds),
        },
      });
      const failures = data.failures || [];
      if (failures.length > 0) {
        setResult({ success: false, message: failures[0]?.message || "Visier rejected the request.", data });
      } else {
        setResult({
          success: true,
          message: `${selNewPerms.length} permission${selNewPerms.length !== 1 ? "s" : ""} added to "${group?.displayName}".`,
          data,
        });
        setSelNewPerms([]);
      }
    } catch (err) {
      setResult({ success: false, message: err.message, incident: err.data?.serviceNowIncident });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      {/* Group selector */}
      <SingleSelect
        label="User Group"
        items={userGroups}
        value={selectedGroup}
        onChange={setSelectedGroup}
        loading={loadingGroups}
        placeholder={loadingGroups ? "Loading groups…" : `Select a user group (${userGroups.length} available)`}
      />

      {selectedGroup && (
        <>
          {/* Already assigned — read only */}
          <div className="cug-field">
            <label className="control-label">
              Already Assigned
              <span className="cug-count-badge">{existingIds.length}</span>
            </label>
            {existingPerms.length === 0
              ? <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-muted)" }}>None</div>
              : (
                <div className="cug-tags">
                  {existingPerms.map(p => (
                    <span key={p.id} className="cug-tag"
                      style={{ background: "var(--bg-raised)", color: "var(--text-muted)", borderColor: "var(--border-light)" }}>
                      {p.displayName}
                    </span>
                  ))}
                </div>
              )}
          </div>

          <div className="cug-divider" />

          {/* Available to add */}
          <MultiSelect
            label="Add Permissions"
            items={availablePerms}
            selected={selNewPerms}
            onToggle={togglePerm}
            loading={loadingPerms}
            emptyLabel={
              availablePerms.length === 0
                ? "All permissions are already assigned to this group"
                : `Select permissions to add (${availablePerms.length} available)`
            }
          />

          <div className="cug-actions">
            <button className="compare-btn" onClick={handleSubmit}
              disabled={submitting || selNewPerms.length === 0 || availablePerms.length === 0}>
              {submitting ? <><span className="spinner" /> Saving…</> : `Add ${selNewPerms.length > 0 ? selNewPerms.length + " " : ""}Permission${selNewPerms.length !== 1 ? "s" : ""}`}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 3: View Group Permissions ──────────────────────────────────────────
function ViewPermissionsTab({ permissions, loadingPerms, userGroups, loadingGroups }) {
  const [selectedGroup, setSelectedGroup] = useState("");
  const group = userGroups.find(g => g.id === selectedGroup);
  const assignedPerms = group ? permissions.filter(p => (group.permissionIds || []).includes(p.id)) : [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <SingleSelect
        label="User Group"
        items={userGroups}
        value={selectedGroup}
        onChange={setSelectedGroup}
        loading={loadingGroups}
        placeholder={loadingGroups ? "Loading groups…" : `Select a user group (${userGroups.length} available)`}
      />
      {selectedGroup && (
        <>
          <div className="cug-divider" />
          <div>
            <label className="control-label" style={{ marginBottom: "12px", display: "block" }}>
              Assigned Permissions
              <span className="cug-count-badge">{assignedPerms.length}</span>
            </label>
            {assignedPerms.length === 0
              ? <div style={{ fontFamily: "var(--font-mono)", fontSize: "12px", color: "var(--text-muted)", padding: "16px 0" }}>
                  No permissions assigned to this group.
                </div>
              : (
                <div className="vp-table">
                  <div className="vp-header">
                    <div className="vp-col">Permission Name</div>
                    <div className="vp-col vp-col-id">Permission ID</div>
                  </div>
                  {assignedPerms.map(p => (
                    <div key={p.id} className="vp-row">
                      <div className="vp-col">{p.displayName}</div>
                      <div className="vp-col vp-col-id">{p.id}</div>
                    </div>
                  ))}
                </div>
              )}
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Unassign Permissions ──────────────────────────────────────────────
function UnassignPermissionsTab({ permissions, loadingPerms, userGroups, loadingGroups }) {
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selRemovePerms, setSelRemovePerms] = useState([]);
  const [submitting, setSubmitting]         = useState(false);
  const [result, setResult]                 = useState(null);

  useEffect(() => { setSelRemovePerms([]); setResult(null); }, [selectedGroup]);

  const group         = userGroups.find(g => g.id === selectedGroup);
  const existingIds   = group?.permissionIds || [];
  const existingPerms = permissions.filter(p => existingIds.includes(p.id));

  const togglePerm = useCallback(id =>
    setSelRemovePerms(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]), []);

  const handleSubmit = async () => {
    if (!selectedGroup) { setResult({ success: false, message: "Please select a user group." }); return; }
    if (selRemovePerms.length === 0) { setResult({ success: false, message: "Please select at least one permission to remove." }); return; }
    setSubmitting(true); setResult(null);
    try {
      // Subtractive: existing minus the ones being removed
      const remainingIds = existingIds.filter(id => !selRemovePerms.includes(id));
      const data = await apiPut("/user-groups/permissions", {
        userGroupId: selectedGroup,
        permissionIds: remainingIds,
        audit: {
          action: "unassign",
          groupName: group?.displayName,
          removedPermissions: permissionNames(permissions, selRemovePerms),
          resultingPermissionNames: permissionNames(permissions, remainingIds),
        },
      });
      const failures = data.failures || [];
      if (failures.length > 0) {
        setResult({ success: false, message: failures[0]?.message || "Visier rejected the request.", data });
      } else {
        setResult({
          success: true,
          message: `${selRemovePerms.length} permission${selRemovePerms.length !== 1 ? "s" : ""} removed from "${group?.displayName}".`,
          data,
        });
        setSelRemovePerms([]);
      }
    } catch (err) {
      setResult({ success: false, message: err.message, incident: err.data?.serviceNowIncident });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <SingleSelect
        label="User Group"
        items={userGroups}
        value={selectedGroup}
        onChange={setSelectedGroup}
        loading={loadingGroups}
        placeholder={loadingGroups ? "Loading groups…" : `Select a user group (${userGroups.length} available)`}
      />

      {selectedGroup && (
        <>
          <MultiSelect
            label="Remove Permissions"
            items={existingPerms}
            selected={selRemovePerms}
            onToggle={togglePerm}
            loading={loadingPerms}
            emptyLabel={
              existingPerms.length === 0
                ? "This group has no permissions assigned"
                : `Select permissions to remove (${existingPerms.length} assigned)`
            }
          />

          {selRemovePerms.length > 0 && (
            <div style={{
              background: "rgba(239,68,68,0.06)", border: "1px solid rgba(239,68,68,0.25)",
              borderRadius: "var(--radius-md)", padding: "12px 14px",
              fontSize: "12px", color: "#ef4444",
            }}>
              ⚠ Removing {selRemovePerms.length} permission{selRemovePerms.length !== 1 ? "s" : ""} from "{group?.displayName}". This takes effect immediately.
            </div>
          )}

          <div className="cug-actions">
            <button
              onClick={handleSubmit}
              disabled={submitting || selRemovePerms.length === 0 || existingPerms.length === 0}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 20px", borderRadius: "6px", border: "none",
                background: selRemovePerms.length > 0 ? "#ef4444" : "var(--bg-raised)",
                color: selRemovePerms.length > 0 ? "white" : "var(--text-muted)",
                fontSize: "13px", fontWeight: 500,
                cursor: selRemovePerms.length > 0 && !submitting ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {submitting ? <><span className="spinner" /> Saving…</> : (
                `Remove ${selRemovePerms.length > 0 ? selRemovePerms.length + " " : ""}Permission${selRemovePerms.length !== 1 ? "s" : ""}`
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 4: Delete Group ────────────────────────────────────────────────────
function DeleteGroupTab({ userGroups, loadingGroups, onDeleted }) {
  const [selectedGroup, setSelectedGroup] = useState("");
  const [confirmed, setConfirmed]         = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [result, setResult]               = useState(null);

  const group = userGroups.find(g => g.id === selectedGroup);

  // Reset confirmation whenever selection changes
  const handleSelect = (id) => { setSelectedGroup(id); setConfirmed(false); setResult(null); };

  const handleDelete = async () => {
    if (!group || !confirmed) return;
    setSubmitting(true); setResult(null);
    try {
      const data = await apiDelete("/nonversioned/user-groups", {
        userGroups: [{ userGroupId: group.id }],
        audit: { groupName: group.displayName },
      });
      setResult({ success: true, message: `"${group.displayName}" was deleted successfully.`, data });
      setSelectedGroup(""); setConfirmed(false);
      if (onDeleted) onDeleted();
    } catch (err) {
      setResult({ success: false, message: err.message, incident: err.data?.serviceNowIncident });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <SingleSelect
        label="User Group"
        items={userGroups}
        value={selectedGroup}
        onChange={handleSelect}
        loading={loadingGroups}
        placeholder={loadingGroups ? "Loading groups…" : `Select a user group to delete (${userGroups.length} available)`}
      />

      {group && (
        <>
          {/* Group info card */}
          <div style={{
            background: "var(--bg-raised)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 18px",
            display: "flex", flexDirection: "column", gap: "8px",
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: "4px" }}>
              Selected Group
            </div>
            <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 500 }}>{group.displayName}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{group.id}</div>
            {group.permissionIds?.length > 0 && (
              <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
                ⚠ This group has <strong style={{ color: "var(--text-secondary)" }}>{group.permissionIds.length} permission{group.permissionIds.length !== 1 ? "s" : ""}</strong> assigned. Deleting it will remove all associations.
              </div>
            )}
          </div>

          {/* Confirmation checkbox */}
          <div
            onClick={() => setConfirmed(c => !c)}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              cursor: "pointer", userSelect: "none",
              padding: "12px 14px",
              border: `1px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "var(--radius-md)",
              background: confirmed ? "rgba(239,68,68,0.06)" : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span style={{
              width: "16px", height: "16px", flexShrink: 0,
              border: `2px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center",
              background: confirmed ? "#ef4444" : "transparent",
              transition: "all 0.15s",
            }}>
              {confirmed && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
                  <polyline points="2 6 5 9 10 3"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: "13px", color: confirmed ? "#ef4444" : "var(--text-secondary)" }}>
              I understand this action is permanent and cannot be undone
            </span>
          </div>

          {/* Delete button */}
          <div className="cug-actions">
            <button
              onClick={handleDelete}
              disabled={!confirmed || submitting}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 20px", borderRadius: "6px", border: "none",
                background: confirmed ? "#ef4444" : "var(--bg-raised)",
                color: confirmed ? "white" : "var(--text-muted)",
                fontSize: "13px", fontWeight: 500, cursor: confirmed ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {submitting ? <><span className="spinner" /> Deleting…</> : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Delete Group
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 5: Delete Permission ───────────────────────────────────────────────
function DeletePermissionTab({ permissions, loadingPerms, onDeleted }) {
  const [selectedPerm, setSelectedPerm] = useState("");
  const [confirmed, setConfirmed]       = useState(false);
  const [submitting, setSubmitting]     = useState(false);
  const [result, setResult]             = useState(null);

  const perm = permissions.find(p => p.id === selectedPerm);

  const handleSelect = (id) => { setSelectedPerm(id); setConfirmed(false); setResult(null); };

  const handleDelete = async () => {
    if (!perm || !confirmed) return;
    setSubmitting(true); setResult(null);
    try {
      const data = await apiDelete("/nonversioned/permissions", {
        permissionIds: [perm.id],
        audit: { permissionName: perm.displayName },
      });
      setResult({ success: true, message: `"${perm.displayName}" was deleted successfully.`, data });
      setSelectedPerm(""); setConfirmed(false);
      if (onDeleted) onDeleted();
    } catch (err) {
      setResult({ success: false, message: err.message, incident: err.data?.serviceNowIncident });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <SingleSelect
        label="Permission"
        items={permissions}
        value={selectedPerm}
        onChange={handleSelect}
        loading={loadingPerms}
        placeholder={loadingPerms ? "Loading permissions…" : `Select a permission to delete (${permissions.length} available)`}
      />

      {perm && (
        <>
          <div style={{
            background: "var(--bg-raised)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 18px",
            display: "flex", flexDirection: "column", gap: "8px",
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: "4px" }}>
              Selected Permission
            </div>
            <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 500 }}>{perm.displayName}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{perm.id}</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
              ⚠ Deleting this permission will remove it from any user groups it is currently assigned to.
            </div>
          </div>

          <div
            onClick={() => setConfirmed(c => !c)}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              cursor: "pointer", userSelect: "none", padding: "12px 14px",
              border: `1px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "var(--radius-md)",
              background: confirmed ? "rgba(239,68,68,0.06)" : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span style={{
              width: "16px", height: "16px", flexShrink: 0,
              border: `2px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center",
              background: confirmed ? "#ef4444" : "transparent",
              transition: "all 0.15s",
            }}>
              {confirmed && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
                  <polyline points="2 6 5 9 10 3"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: "13px", color: confirmed ? "#ef4444" : "var(--text-secondary)" }}>
              I understand this action is permanent and cannot be undone
            </span>
          </div>

          <div className="cug-actions">
            <button
              onClick={handleDelete}
              disabled={!confirmed || submitting}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 20px", borderRadius: "6px", border: "none",
                background: confirmed ? "#ef4444" : "var(--bg-raised)",
                color: confirmed ? "white" : "var(--text-muted)",
                fontSize: "13px", fontWeight: 500, cursor: confirmed ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {submitting ? <><span className="spinner" /> Deleting…</> : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Delete Permission
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab: Population Access ───────────────────────────────────────────────
function PopulationAccessTab() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [selectedRows, setSelectedRows] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [searchHighlight, setSearchHighlight] = useState("");

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await apiFetch("/population-access");
      setData(response.data || []);
    } catch (err) {
      console.error("Failed to load population access:", err);
      alert(`Failed to load data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setSearchHighlight(searchText.trim());
  };

  const handleClearSearch = () => {
    setSearchText("");
    setSearchHighlight("");
    setSelectedRows(new Set());
  };

  const toggleRow = (index) => {
    const newSelected = new Set(selectedRows);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedRows(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedRows.size === filteredData.length) {
      setSelectedRows(new Set());
    } else {
      setSelectedRows(new Set(filteredData.map((_, i) => i)));
    }
  };

  const toggleExpand = (index) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedRows(newExpanded);
  };

  const exportData = (rows) => {
    if (rows.length === 0) {
      alert("No data to export");
      return;
    }

    const csvRows = [
      ["Type", "Code", "Description", "User Count", "Employee IDs"],
      ...rows.map(row => [
        row.security_type,
        row.security_value,
        row.security_desc,
        row.user_count,
        row.actor_list.join(", ")
      ])
    ];

    const csvContent = csvRows.map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(",")
    ).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    const timestamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, -5);
    link.setAttribute("href", url);
    link.setAttribute("download", `population-access-${timestamp}.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleExportAll = () => {
    exportData(filteredData);
  };

  const handleExportSelected = () => {
    const selected = filteredData.filter((_, i) => selectedRows.has(i));
    exportData(selected);
  };

  // Filter data based on search
  const filteredData = data.filter(row => {
    if (!searchHighlight) return true;
    const search = searchHighlight.toLowerCase();
    return row.actor_list.some(id => id.toLowerCase().includes(search));
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      {/* Search Section */}
      <div style={{ display: "flex", gap: "12px", alignItems: "flex-end" }}>
        <div style={{ flex: 1 }}>
          <label className="control-label" style={{ display: "block", marginBottom: "6px" }}>
            Search by Employee ID
          </label>
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
            placeholder="Enter employee ID to highlight rows..."
            className="cug-input"
          />
        </div>
        <button onClick={handleSearch} className="compare-btn" style={{ marginBottom: "2px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/>
            <path d="m21 21-4.35-4.35"/>
          </svg>
          Search
        </button>
        {searchHighlight && (
          <button onClick={handleClearSearch} className="compare-btn" style={{ marginBottom: "2px" }}>
            Clear
          </button>
        )}
        <button onClick={loadData} disabled={loading} className="compare-btn" style={{ marginBottom: "2px" }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
          </svg>
          Reload
        </button>
      </div>

      {/* Export Buttons */}
      <div style={{ display: "flex", gap: "12px" }}>
        <button onClick={handleExportAll} disabled={filteredData.length === 0} className="compare-btn">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          Export All ({filteredData.length})
        </button>
        <button 
          onClick={handleExportSelected} 
          disabled={selectedRows.size === 0} 
          className="compare-btn"
          style={{ background: selectedRows.size > 0 ? "#70cc98" : "var(--bg-raised)", color: selectedRows.size > 0 ? "white" : "var(--text-muted)" }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M7 10l5 5 5-5M12 15V3"/>
          </svg>
          Export Selected ({selectedRows.size})
        </button>
      </div>

      {/* Data Table */}
      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
          <span className="spinner" style={{ marginRight: "8px" }} />
          Loading population access data...
        </div>
      ) : filteredData.length === 0 ? (
        <div style={{ padding: "40px", textAlign: "center", color: "var(--text-muted)" }}>
          {searchHighlight ? `No records found for employee ID: ${searchHighlight}` : "No data available"}
        </div>
      ) : (
        <div style={{ border: "1px solid var(--border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "var(--bg-raised)", borderBottom: "1px solid var(--border)" }}>
                <th style={{ padding: "12px 16px", textAlign: "center", width: "50px" }}>
                  <input 
                    type="checkbox"
                    checked={selectedRows.size === filteredData.length && filteredData.length > 0}
                    onChange={toggleSelectAll}
                    style={{ cursor: "pointer" }}
                  />
                </th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>TYPE</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>CODE</th>
                <th style={{ padding: "12px 16px", textAlign: "left", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>DESCRIPTION</th>
                <th style={{ padding: "12px 16px", textAlign: "right", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>USER COUNT</th>
                <th style={{ padding: "12px 16px", textAlign: "center", fontSize: "10px", fontWeight: 600, color: "#8ba2b8", textTransform: "uppercase", letterSpacing: "0.07em" }}>VIEW USERS</th>
              </tr>
            </thead>
            <tbody>
              {filteredData.map((row, index) => {
                const isSelected = selectedRows.has(index);
                const isExpanded = expandedRows.has(index);
                const isHighlighted = searchHighlight && row.actor_list.some(id => 
                  id.toLowerCase().includes(searchHighlight.toLowerCase())
                );

                return (
                  <React.Fragment key={index}>
                    <tr 
                      style={{ 
                        borderBottom: "1px solid var(--border)",
                        background: isHighlighted ? "rgba(112, 204, 152, 0.1)" : (isSelected ? "rgba(112, 204, 152, 0.05)" : "transparent"),
                        transition: "background 0.15s"
                      }}
                    >
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <input 
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRow(index)}
                          style={{ cursor: "pointer" }}
                        />
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
                        {row.security_type}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "var(--text-primary)", fontFamily: "var(--font-mono)" }}>
                        {row.security_value}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "var(--text-primary)", fontWeight: 500 }}>
                        {row.security_desc}
                      </td>
                      <td style={{ padding: "12px 16px", fontSize: "13px", color: "var(--text-primary)", textAlign: "right" }}>
                        {row.user_count.toLocaleString()}
                      </td>
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        <button
                          onClick={() => toggleExpand(index)}
                          style={{
                            background: "transparent",
                            border: "1px solid var(--border)",
                            borderRadius: "4px",
                            padding: "4px 12px",
                            fontSize: "12px",
                            color: "#70cc98",
                            cursor: "pointer",
                            transition: "all 0.2s"
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.background = "rgba(112, 204, 152, 0.1)"}
                          onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                        >
                          {isExpanded ? "Hide" : "View"} ({row.user_count})
                        </button>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr style={{ borderBottom: "1px solid var(--border)" }}>
                        <td colSpan="6" style={{ padding: "16px", background: "var(--bg-raised)" }}>
                          <div style={{ fontSize: "11px", color: "var(--text-muted)", marginBottom: "8px", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                            Employee IDs ({row.user_count})
                          </div>
                          <div style={{ 
                            maxHeight: "200px", 
                            overflow: "auto", 
                            fontFamily: "var(--font-mono)", 
                            fontSize: "11px", 
                            color: "var(--text-primary)",
                            lineHeight: "1.6",
                            padding: "12px",
                            background: "var(--bg-secondary)",
                            borderRadius: "4px",
                            border: "1px solid var(--border)"
                          }}>
                            {row.actor_list.map((id, i) => {
                              const isMatch = searchHighlight && id.toLowerCase().includes(searchHighlight.toLowerCase());
                              return (
                                <span 
                                  key={i}
                                  style={{
                                    display: "inline-block",
                                    padding: "2px 6px",
                                    margin: "2px",
                                    background: isMatch ? "rgba(112, 204, 152, 0.2)" : "transparent",
                                    borderRadius: "3px",
                                    border: isMatch ? "1px solid #70cc98" : "none"
                                  }}
                                >
                                  {id}
                                  {i < row.actor_list.length - 1 ? "," : ""}
                                </span>
                              );
                            })}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Tab 6: Versioned Add Users ───────────────────────────────────────────
function VersionedAddUsersTab() {
  const [projectId, setProjectId] = useState("");
  const [userGroupId, setUserGroupId] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [draftGroups, setDraftGroups] = useState([]);
  const [loadingDraftGroups, setLoadingDraftGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingProjects(true);
    apiFetch("/projects")
      .then((data) => {
        if (cancelled) return;
        const items = (data.projects || []).map((p) => ({
          id: p.id,
          displayName: p.displayName || p.id,
        }));
        setProjects(items);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load draft projects: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setUserGroupId("");
    setDraftGroups([]);

    if (!projectId) {
      return () => { cancelled = true; };
    }

    setLoadingDraftGroups(true);
    apiFetch(`/user-groups?projectId=${encodeURIComponent(projectId)}`)
      .then((draftData) => {
        if (cancelled) return;
        const groups = (draftData.userGroups || []).map((g) => ({
          id: g.userGroupId,
          displayName: g.displayName || g.userGroupId,
        }));
        setDraftGroups(groups);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load groups for selected project: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingDraftGroups(false);
      });

    return () => { cancelled = true; };
  }, [projectId]);

  const parsedEmails = emailsText
    .split(/[\n,;\s]+/)
    .map(v => v.trim())
    .filter(Boolean);

  const handleSubmit = async () => {
    const submittedProjectId = projectId.trim();
    const submittedUserGroupId = userGroupId.trim();
    const submittedGroupName = selectedGroup?.displayName || submittedUserGroupId;
    const submittedEmails = [...new Set(parsedEmails.map((v) => v.toLowerCase()))];

    if (!submittedProjectId) {
      setResult({ success: false, message: "Draft projectId is required." });
      return;
    }
    if (!submittedUserGroupId) {
      setResult({ success: false, message: "userGroupId is required." });
      return;
    }
    if (submittedEmails.length === 0) {
      setResult({ success: false, message: "Paste at least one email." });
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const data = await apiPost("/versioned/user-groups/users", {
        projectId: submittedProjectId,
        userGroupId: submittedUserGroupId,
        emailsText,
        audit: { groupName: submittedGroupName, projectName: projects.find((p) => p.id === submittedProjectId)?.displayName },
      });

      const unresolvedEmailsRaw = Array.isArray(data.unresolvedEmails) ? data.unresolvedEmails : [];
      const unresolvedEmails = [...new Set(unresolvedEmailsRaw.map((v) => String(v).toLowerCase()))];
      const unresolvedSet = new Set(unresolvedEmails);
      const addedEmails = submittedEmails.filter((email) => !unresolvedSet.has(email));

      const message =
        `Added ${addedEmails.length} user${addedEmails.length !== 1 ? "s" : ""} ` +
        `to group ${submittedGroupName}: ${addedEmails.join(", ")}.`;

      const warningMessage = unresolvedEmails.length > 0
        ? `${unresolvedEmails.length} user${unresolvedEmails.length !== 1 ? "s" : ""} not added (no Visier user exists): ${unresolvedEmails.join(", ")}.`
        : "";

      setResult({
        success: true,
        message,
        warningMessage,
        hideApiResponse: true,
        data,
      });

      // Clear fields after success so the tab is ready for a fresh add action.
      setProjectId("");
      setUserGroupId("");
      setEmailsText("");
    } catch (e) {
      if (isTimeoutLikeError(e)) {
        setResult({
          success: false,
          message: `Request timed out before confirmation for group ${submittedGroupName}.`,
          warningMessage: "The add operation may or may not have completed. Verify group membership before retrying.",
          hideApiResponse: true,
        });
      } else {
        setResult({ success: false, message: e.message, incident: e.data?.serviceNowIncident });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setProjectId("");
    setUserGroupId("");
    setEmailsText("");
    setResult(null);
  };

  const selectedGroup = draftGroups.find((g) => g.id === userGroupId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <div style={{
        background: "var(--bg-raised)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "12px 14px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}>
        Versioned mode: this updates an existing user group in a draft project using projectId.
      </div>

      <div className="cug-field">
        <SingleSelect
          label="Draft Project"
          items={projects}
          value={projectId}
          onChange={(id) => { setProjectId(id); setResult(null); }}
          loading={loadingProjects}
          placeholder={loadingProjects ? "Loading draft projects…" : `Select draft project (${projects.length} available)`}
        />
      </div>

      <div className="cug-field">
        <SingleSelect
          label="User Group"
          items={draftGroups}
          value={userGroupId}
          onChange={(id) => { setUserGroupId(id); setResult(null); }}
          loading={loadingDraftGroups}
          placeholder={
            projectId
              ? (loadingDraftGroups ? "Loading groups…" : `Select group (${draftGroups.length} available)`)
              : "Select draft project first"
          }
        />
      </div>

      {selectedGroup && (
        <div style={{
          background: "var(--bg-raised)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          padding: "10px 12px",
          fontFamily: "var(--font-mono)",
          fontSize: "11px",
          color: "var(--text-muted)",
        }}>
          Group ID: {selectedGroup.id}
        </div>
      )}

      <div className="cug-field">
        <label className="control-label">Draft Project ID (read-only)</label>
        <input className="cug-input" value={projectId} readOnly placeholder="Selected from Draft Project dropdown" />
      </div>

      <div className="cug-field">
        <label className="control-label">User Emails <span style={{ color: "var(--red)" }}>*</span>
          {parsedEmails.length > 0 && <span className="cug-count-badge">{parsedEmails.length} parsed</span>}
        </label>
        <textarea
          className="cug-input cug-textarea"
          rows={8}
          value={emailsText}
          onChange={e => setEmailsText(e.target.value)}
          placeholder={"Paste one or many emails. New line, comma, semicolon, or space separated."}
        />
      </div>

      <div className="cug-actions">
        <button
          className="compare-btn"
          onClick={handleSubmit}
          disabled={submitting || !projectId.trim() || !userGroupId.trim() || parsedEmails.length === 0}
        >
          {submitting ? <><span className="spinner" /> Adding Users…</> : "Add Users to Versioned Group"}
        </button>
        <button className="cug-reset-btn" onClick={handleReset}>Reset</button>
      </div>
    </div>
  );
}

// ── Tab 7: Versioned Delete Users ────────────────────────────────────────
function VersionedDeleteUsersTab() {
  const [projectId, setProjectId] = useState("");
  const [userGroupId, setUserGroupId] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const [draftGroups, setDraftGroups] = useState([]);
  const [loadingDraftGroups, setLoadingDraftGroups] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingProjects(true);
    apiFetch("/projects")
      .then((data) => {
        if (cancelled) return;
        const items = (data.projects || []).map((p) => ({
          id: p.id,
          displayName: p.displayName || p.id,
        }));
        setProjects(items);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load draft projects: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingProjects(false);
      });

    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setUserGroupId("");
    setConfirmed(false);
    setDraftGroups([]);

    if (!projectId) {
      return () => { cancelled = true; };
    }

    setLoadingDraftGroups(true);
    apiFetch(`/user-groups?projectId=${encodeURIComponent(projectId)}`)
      .then((draftData) => {
        if (cancelled) return;
        const groups = (draftData.userGroups || []).map((g) => ({
          id: g.userGroupId,
          displayName: g.displayName || g.userGroupId,
        }));
        setDraftGroups(groups);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load groups for selected project: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingDraftGroups(false);
      });

    return () => { cancelled = true; };
  }, [projectId]);

  const parsedEmails = emailsText
    .split(/[\n,;\s]+/)
    .map(v => v.trim())
    .filter(Boolean);

  const selectedGroup = draftGroups.find((g) => g.id === userGroupId);

  const handleSelect = (id) => { setUserGroupId(id); setConfirmed(false); setResult(null); };

  const handleDelete = async () => {
    if (!selectedGroup || !confirmed) return;

    const submittedProjectId = projectId.trim();
    const submittedUserGroupId = userGroupId.trim();
    const submittedGroupName = selectedGroup.displayName || submittedUserGroupId;
    const submittedEmails = [...new Set(parsedEmails.map((v) => v.toLowerCase()))];

    if (!submittedProjectId) {
      setResult({ success: false, message: "Draft projectId is required." });
      return;
    }
    if (!submittedUserGroupId) {
      setResult({ success: false, message: "userGroupId is required." });
      return;
    }
    if (submittedEmails.length === 0) {
      setResult({ success: false, message: "Paste at least one email." });
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const data = await apiDelete("/versioned/user-groups/users", {
        projectId: submittedProjectId,
        userGroupId: submittedUserGroupId,
        emailsText,
        audit: { groupName: submittedGroupName, projectName: projects.find((p) => p.id === submittedProjectId)?.displayName },
      });

      const unresolvedEmailsRaw = Array.isArray(data.unresolvedEmails) ? data.unresolvedEmails : [];
      const unresolvedEmails = [...new Set(unresolvedEmailsRaw.map((v) => String(v).toLowerCase()))];
      const unresolvedSet = new Set(unresolvedEmails);
      const removedEmails = submittedEmails.filter((email) => !unresolvedSet.has(email));

      const message = removedEmails.length > 0
        ? `Removed ${removedEmails.length} user${removedEmails.length !== 1 ? "s" : ""} from "${submittedGroupName}": ${removedEmails.join(", ")}.`
        : "No users were removed.";

      const warningMessage = unresolvedEmails.length > 0
        ? `⚠ ${unresolvedEmails.length} email${unresolvedEmails.length !== 1 ? "s" : ""} could not be matched to Visier users: ${unresolvedEmails.join(", ")}`
        : "";

      setResult({
        success: true,
        message,
        warningMessage,
        hideApiResponse: true,
        data,
      });

      setProjectId("");
      setUserGroupId("");
      setEmailsText("");
      setConfirmed(false);
    } catch (e) {
      if (isTimeoutLikeError(e)) {
        setResult({
          success: false,
          message: `Request timed out before confirmation for group ${submittedGroupName}.`,
          warningMessage: "The remove operation may or may not have completed. Verify group membership before retrying.",
          hideApiResponse: true,
        });
      } else {
        setResult({ success: false, message: e.message, incident: e.data?.serviceNowIncident });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <SingleSelect
        label="Draft Project"
        items={projects}
        value={projectId}
        onChange={(id) => { setProjectId(id); setResult(null); }}
        loading={loadingProjects}
        placeholder={loadingProjects ? "Loading draft projects…" : `Select draft project (${projects.length} available)`}
      />

      {projectId && (
        <SingleSelect
          label="User Group"
          items={draftGroups}
          value={userGroupId}
          onChange={handleSelect}
          loading={loadingDraftGroups}
          placeholder={loadingDraftGroups ? "Loading groups…" : `Select group to remove users from (${draftGroups.length} available)`}
        />
      )}

      {selectedGroup && (
        <>
          {/* Group info card */}
          <div style={{
            background: "var(--bg-raised)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 18px",
            display: "flex", flexDirection: "column", gap: "8px",
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: "4px" }}>
              Selected Group
            </div>
            <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 500 }}>{selectedGroup.displayName}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{selectedGroup.id}</div>
            <div style={{ fontSize: "12px", color: "var(--text-muted)", marginTop: "4px" }}>
              ⚠ Removing {parsedEmails.length} user{parsedEmails.length !== 1 ? "s" : ""} from this group in the draft project. This action will take effect when the project is published.
            </div>
          </div>

          {/* User emails display */}
          {parsedEmails.length > 0 && (
            <div style={{
              background: "var(--bg-raised)", border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)", padding: "12px 14px",
              fontSize: "12px", color: "var(--text-secondary)",
            }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>Users to remove ({parsedEmails.length}):</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", lineHeight: 1.6 }}>
                {parsedEmails.join("\n")}
              </div>
            </div>
          )}

          {/* Email input for editing */}
          <div>
            <label className="control-label">User Emails <span style={{ color: "var(--red)" }}>*</span>
              {parsedEmails.length > 0 && <span className="cug-count-badge">{parsedEmails.length} parsed</span>}
            </label>
            <textarea
              className="cug-input cug-textarea"
              rows={4}
              value={emailsText}
              onChange={e => { setEmailsText(e.target.value); setConfirmed(false); }}
              placeholder={"Paste emails to remove. New line, comma, semicolon, or space separated."}
            />
          </div>

          {/* Confirmation checkbox */}
          <div
            onClick={() => setConfirmed(c => !c)}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              cursor: "pointer", userSelect: "none",
              padding: "12px 14px",
              border: `1px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "var(--radius-md)",
              background: confirmed ? "rgba(239,68,68,0.06)" : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span style={{
              width: "16px", height: "16px", flexShrink: 0,
              border: `2px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center",
              background: confirmed ? "#ef4444" : "transparent",
              transition: "all 0.15s",
            }}>
              {confirmed && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
                  <polyline points="2 6 5 9 10 3"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: "13px", color: confirmed ? "#ef4444" : "var(--text-secondary)" }}>
              I understand this action is permanent and cannot be undone
            </span>
          </div>

          {/* Delete button */}
          <div className="cug-actions">
            <button
              onClick={handleDelete}
              disabled={!confirmed || submitting || parsedEmails.length === 0}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 20px", borderRadius: "6px", border: "none",
                background: confirmed && parsedEmails.length > 0 ? "#ef4444" : "var(--bg-raised)",
                color: confirmed && parsedEmails.length > 0 ? "white" : "var(--text-muted)",
                fontSize: "13px", fontWeight: 500, cursor: confirmed && parsedEmails.length > 0 ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {submitting ? <><span className="spinner" /> Removing…</> : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Remove Users
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Tab 8: NonVersioned Add Users ─────────────────────────────────────────
function NonVersionedAddUsersTab() {
  const [userGroupId, setUserGroupId] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [nonVersionedGroups, setNonVersionedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingGroups(true);
    apiFetch("/nonversioned/user-groups")
      .then((data) => {
        if (cancelled) return;
        const items = (data.userGroups || []).map((g) => ({
          id: g.userGroupId,
          displayName: (g.displayName || g.userGroupId) + " <<NonVersioned>>",
        }));
        setNonVersionedGroups(items);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load non-versioned user groups: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingGroups(false);
      });

    return () => { cancelled = true; };
  }, []);

  const parsedEmails = emailsText
    .split(/[\n,;\s]+/)
    .map(v => v.trim())
    .filter(Boolean);

  const handleSubmit = async () => {
    const submittedUserGroupId = userGroupId.trim();
    const submittedGroupName = selectedGroup?.displayName || submittedUserGroupId;
    const submittedEmails = [...new Set(parsedEmails.map((v) => v.toLowerCase()))];

    if (!submittedUserGroupId) {
      setResult({ success: false, message: "userGroupId is required." });
      return;
    }
    if (submittedEmails.length === 0) {
      setResult({ success: false, message: "Paste at least one email." });
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const data = await apiPost("/nonversioned/user-groups/users", {
        userGroupId: submittedUserGroupId,
        emailsText,
        audit: { groupName: submittedGroupName },
      });

      const unresolvedEmailsRaw = Array.isArray(data.unresolvedEmails) ? data.unresolvedEmails : [];
      const unresolvedEmails = [...new Set(unresolvedEmailsRaw.map((v) => String(v).toLowerCase()))];
      const unresolvedSet = new Set(unresolvedEmails);
      const addedEmails = submittedEmails.filter((email) => !unresolvedSet.has(email));

      const message =
        `Added ${addedEmails.length} user${addedEmails.length !== 1 ? "s" : ""} ` +
        `to group ${submittedGroupName}: ${addedEmails.join(", ")}.`;

      const warningMessage = unresolvedEmails.length > 0
        ? `${unresolvedEmails.length} user${unresolvedEmails.length !== 1 ? "s" : ""} not added (no Visier user exists): ${unresolvedEmails.join(", ")}.`
        : "";

      setResult({
        success: true,
        message,
        warningMessage,
        hideApiResponse: true,
        data,
      });

      // Clear fields after success so the tab is ready for a fresh add action.
      setUserGroupId("");
      setEmailsText("");
    } catch (e) {
      if (isTimeoutLikeError(e)) {
        setResult({
          success: false,
          message: `Request timed out before confirmation for group ${submittedGroupName}.`,
          warningMessage: "The add operation may or may not have completed. Verify group membership before retrying.",
          hideApiResponse: true,
        });
      } else {
        setResult({ success: false, message: e.message, incident: e.data?.serviceNowIncident });
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = () => {
    setUserGroupId("");
    setEmailsText("");
    setResult(null);
  };

  const selectedGroup = nonVersionedGroups.find((g) => g.id === userGroupId);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <div style={{
        background: "var(--bg-raised)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "12px 14px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}>
        <span className="cug-nonversioned-badge">NonVersioned: true</span>
        &nbsp;· Takes effect immediately in production · No new version published
      </div>

      <div className="cug-field">
        <SingleSelect
          label="User Group (Production)"
          items={nonVersionedGroups}
          value={userGroupId}
          onChange={(id) => { setUserGroupId(id); setResult(null); }}
          loading={loadingGroups}
          placeholder={loadingGroups ? "Loading production groups…" : `Select production group (${nonVersionedGroups.length} available)`}
        />
      </div>

      {selectedGroup && (
        <div style={{
          background: "var(--bg-raised)",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-md)",
          padding: "10px 12px",
          fontFamily: "var(--font-mono)",
          fontSize: "11px",
          color: "var(--text-muted)",
        }}>
          Group ID: {selectedGroup.id}
        </div>
      )}

      <div className="cug-field">
        <label className="control-label">User Emails <span style={{ color: "var(--red)" }}>*</span>
          {parsedEmails.length > 0 && <span className="cug-count-badge">{parsedEmails.length} parsed</span>}
        </label>
        <textarea
          className="cug-input cug-textarea"
          rows={8}
          value={emailsText}
          onChange={e => setEmailsText(e.target.value)}
          placeholder={"Paste one or many emails. New line, comma, semicolon, or space separated."}
        />
      </div>

      <div className="cug-actions">
        <button
          className="compare-btn"
          onClick={handleSubmit}
          disabled={submitting || !userGroupId.trim() || parsedEmails.length === 0}
        >
          {submitting ? <><span className="spinner" /> Adding Users…</> : "Add Users to Production Group"}
        </button>
        <button className="cug-reset-btn" onClick={handleReset}>Reset</button>
      </div>
    </div>
  );
}

// ── Tab 9: NonVersioned Delete Users ──────────────────────────────────────
function NonVersionedDeleteUsersTab() {
  const [userGroupId, setUserGroupId] = useState("");
  const [emailsText, setEmailsText] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [nonVersionedGroups, setNonVersionedGroups] = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoadingGroups(true);
    apiFetch("/nonversioned/user-groups")
      .then((data) => {
        if (cancelled) return;
        const items = (data.userGroups || []).map((g) => ({
          id: g.userGroupId,
          displayName: (g.displayName || g.userGroupId) + " <<NonVersioned>>",
        }));
        setNonVersionedGroups(items);
      })
      .catch((e) => {
        if (cancelled) return;
        setResult({ success: false, message: `Failed to load non-versioned user groups: ${e.message}` });
      })
      .finally(() => {
        if (!cancelled) setLoadingGroups(false);
      });

    return () => { cancelled = true; };
  }, []);

  const parsedEmails = emailsText
    .split(/[\n,;\s]+/)
    .map(v => v.trim())
    .filter(Boolean);

  const selectedGroup = nonVersionedGroups.find((g) => g.id === userGroupId);

  const handleSelect = (id) => { setUserGroupId(id); setConfirmed(false); setResult(null); };

  const handleDelete = async () => {
    if (!selectedGroup || !confirmed) return;

    const submittedUserGroupId = userGroupId.trim();
    const submittedGroupName = selectedGroup.displayName || submittedUserGroupId;
    const submittedEmails = [...new Set(parsedEmails.map((v) => v.toLowerCase()))];

    if (!submittedUserGroupId) {
      setResult({ success: false, message: "userGroupId is required." });
      return;
    }
    if (submittedEmails.length === 0) {
      setResult({ success: false, message: "Paste at least one email." });
      return;
    }

    setSubmitting(true);
    setResult(null);
    try {
      const data = await apiDelete("/nonversioned/user-groups/users", {
        userGroupId: submittedUserGroupId,
        emailsText,
        audit: { groupName: submittedGroupName },
      });

      const unresolvedEmailsRaw = Array.isArray(data.unresolvedEmails) ? data.unresolvedEmails : [];
      const unresolvedEmails = [...new Set(unresolvedEmailsRaw.map((v) => String(v).toLowerCase()))];
      const unresolvedSet = new Set(unresolvedEmails);
      const removedEmails = submittedEmails.filter((email) => !unresolvedSet.has(email));

      const message = removedEmails.length > 0
        ? `Removed ${removedEmails.length} user${removedEmails.length !== 1 ? "s" : ""} from "${submittedGroupName}": ${removedEmails.join(", ")}.`
        : "No users were removed.";

      const warningMessage = unresolvedEmails.length > 0
        ? `⚠ ${unresolvedEmails.length} email${unresolvedEmails.length !== 1 ? "s" : ""} could not be matched to Visier users: ${unresolvedEmails.join(", ")}`
        : "";

      setResult({
        success: true,
        message,
        warningMessage,
        hideApiResponse: true,
        data,
      });

      setUserGroupId("");
      setEmailsText("");
      setConfirmed(false);
    } catch (e) {
      if (isTimeoutLikeError(e)) {
        setResult({
          success: false,
          message: `Request timed out before confirmation for group ${submittedGroupName}.`,
          warningMessage: "The remove operation may or may not have completed. Verify group membership before retrying.",
          hideApiResponse: true,
        });
      } else {
        setResult({ success: false, message: e.message, incident: e.data?.serviceNowIncident });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "22px" }}>
      <ResultBanner result={result} onDismiss={() => setResult(null)} />

      <div style={{
        background: "var(--bg-raised)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: "12px 14px",
        fontSize: "12px",
        color: "var(--text-secondary)",
      }}>
        <span className="cug-nonversioned-badge">NonVersioned: true</span>
        &nbsp;· Takes effect immediately in production · No new version published
      </div>

      <SingleSelect
        label="User Group (Production)"
        items={nonVersionedGroups}
        value={userGroupId}
        onChange={handleSelect}
        loading={loadingGroups}
        placeholder={loadingGroups ? "Loading production groups…" : `Select production group (${nonVersionedGroups.length} available)`}
      />

      {selectedGroup && (
        <>
          <div style={{
            background: "var(--bg-raised)", border: "1px solid var(--border)",
            borderRadius: "var(--radius-md)", padding: "16px 18px",
            display: "flex", flexDirection: "column", gap: "8px",
          }}>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "10px", textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--text-muted)", marginBottom: "4px" }}>
              Selected Group
            </div>
            <div style={{ fontSize: "14px", color: "var(--text-primary)", fontWeight: 500 }}>{selectedGroup.displayName}</div>
            <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)" }}>{selectedGroup.id}</div>
            <div style={{ fontSize: "12px", color: "#ef4444", marginTop: "4px", fontWeight: 500 }}>
              ⚠️ Removing {parsedEmails.length} user{parsedEmails.length !== 1 ? "s" : ""} from this production group. This takes effect immediately and cannot be undone.
            </div>
          </div>

          {parsedEmails.length > 0 && (
            <div style={{
              background: "var(--bg-raised)", border: "1px solid var(--border)",
              borderRadius: "var(--radius-md)", padding: "12px 14px",
              fontSize: "12px", color: "var(--text-secondary)",
            }}>
              <div style={{ fontWeight: 500, marginBottom: "8px" }}>Users to remove ({parsedEmails.length}):</div>
              <div style={{ fontFamily: "var(--font-mono)", fontSize: "11px", color: "var(--text-muted)", lineHeight: 1.6 }}>
                {parsedEmails.join("\n")}
              </div>
            </div>
          )}

          <div>
            <label className="control-label">User Emails <span style={{ color: "var(--red)" }}>*</span>
              {parsedEmails.length > 0 && <span className="cug-count-badge">{parsedEmails.length} parsed</span>}
            </label>
            <textarea
              className="cug-input cug-textarea"
              rows={4}
              value={emailsText}
              onChange={e => { setEmailsText(e.target.value); setConfirmed(false); }}
              placeholder={"Paste emails to remove. New line, comma, semicolon, or space separated."}
            />
          </div>

          <div
            onClick={() => setConfirmed(c => !c)}
            style={{
              display: "flex", alignItems: "center", gap: "10px",
              cursor: "pointer", userSelect: "none",
              padding: "12px 14px",
              border: `1px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "var(--radius-md)",
              background: confirmed ? "rgba(239,68,68,0.06)" : "transparent",
              transition: "border-color 0.15s, background 0.15s",
            }}
          >
            <span style={{
              width: "16px", height: "16px", flexShrink: 0,
              border: `2px solid ${confirmed ? "#ef4444" : "var(--border)"}`,
              borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center",
              background: confirmed ? "#ef4444" : "transparent",
              transition: "all 0.15s",
            }}>
              {confirmed && (
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none" stroke="white" strokeWidth="2.5">
                  <polyline points="2 6 5 9 10 3"/>
                </svg>
              )}
            </span>
            <span style={{ fontSize: "13px", color: confirmed ? "#ef4444" : "var(--text-secondary)" }}>
              I understand this action takes effect immediately in production and cannot be undone
            </span>
          </div>

          <div className="cug-actions">
            <button
              onClick={handleDelete}
              disabled={!confirmed || submitting || parsedEmails.length === 0}
              style={{
                display: "flex", alignItems: "center", gap: "8px",
                padding: "8px 20px", borderRadius: "6px", border: "none",
                background: confirmed && parsedEmails.length > 0 ? "#ef4444" : "var(--bg-raised)",
                color: confirmed && parsedEmails.length > 0 ? "white" : "var(--text-muted)",
                fontSize: "13px", fontWeight: 500, cursor: confirmed && parsedEmails.length > 0 ? "pointer" : "not-allowed",
                opacity: submitting ? 0.6 : 1,
                transition: "background 0.15s, color 0.15s",
              }}
            >
              {submitting ? <><span className="spinner" /> Removing…</> : (
                <>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
                  </svg>
                  Remove Users from Production
                </>
              )}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ── Root Component ─────────────────────────────────────────────────────────
export default function CreateUserGroup({ isSuperAdmin = false }) {
  const [activeTab, setActiveTab] = useState("create");

  const [permissions, setPermissions]     = useState([]);
  const [loadingPerms, setLoadingPerms]   = useState(true);
  const [userGroups, setUserGroups]       = useState([]);
  const [loadingGroups, setLoadingGroups] = useState(true);
  const [fetchError, setFetchError]       = useState(null);
  const [refreshing, setRefreshing]       = useState(false);

  useEffect(() => {
    apiFetch("/nonversioned/permissions")
      .then(data => setPermissions((data.permissions || []).map(p => ({
        id: p.permissionId || p.id,
        displayName: p.displayName || p.permissionId || p.id,
      }))))
      .catch(err => setFetchError(err.message))
      .finally(() => setLoadingPerms(false));
  }, []);

  useEffect(() => {
    apiFetch("/nonversioned/user-groups")
      .then(data => setUserGroups((data.userGroups || []).map(g => ({
        id: g.userGroupId,
        displayName: g.displayName,
        permissionIds: g.permissionIds?.ids || g.permissionIds || [],
      }))))
      .catch(err => setFetchError(err.message))
      .finally(() => setLoadingGroups(false));
  }, []);

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setFetchError(null);
    try {
      const [groupsData, permsData] = await Promise.all([
        apiFetch("/nonversioned/user-groups"),
        apiFetch("/nonversioned/permissions"),
      ]);
      setUserGroups((groupsData.userGroups || []).map(g => ({
        id: g.userGroupId,
        displayName: g.displayName,
        permissionIds: g.permissionIds?.ids || g.permissionIds || [],
      })));
      setPermissions((permsData.permissions || []).map(p => ({
        id: p.permissionId || p.id,
        displayName: p.displayName || p.permissionId || p.id,
      })));
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setRefreshing(false);
    }
  }, []);

  const TABS = [
    { id: "create",            label: "Create Group"        },
    { id: "assign",            label: "Assign Permissions"  },
    { id: "unassign",          label: "Unassign Permissions"},
    { id: "view",              label: "View Permissions"    },
    { id: "versioned-add-users", label: "Versioned: Add Users" },
    { id: "versioned-delete-users", label: "Versioned: Delete Users" },
    { id: "nonversioned-add-users", label: "Production: Add Users" },
    { id: "nonversioned-delete-users", label: "Production: Delete Users" },
    { id: "delete-group",      label: "Delete Group"        },
    { id: "delete-permission", label: "Delete Permission"   },
    { id: "population-access", label: "Population Access"   },
    { id: "visier-runbook",    label: "Security Runbook"    },
  ];

  return (
    <div className="cug-root">
      <div className="cug-header">
        <div className="cug-header-top">
          <div className="logo-mark">V</div>
          <div>
            <div className="cug-title">Visier Security Manager</div>
            <div className="cug-subtitle">
              <span className="cug-nonversioned-badge">NonVersioned: true</span>
              &nbsp;· Takes effect immediately · No new production version published
            </div>
          </div>
        </div>
      </div>

      {fetchError && (
        <div className="error-banner" style={{ margin: "16px 32px 0" }}>
          <strong>Failed to load data:</strong> {fetchError}
        </div>
      )}

      <div style={{ padding: "0 32px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div className="tab-bar" style={{ marginBottom: 0 }}>
            {TABS.map(tab => (
              <button key={tab.id}
                className={`tab-btn ${activeTab === tab.id ? "active" : ""} ${tab.id.startsWith("delete") ? "tab-btn-danger" : ""}`}
                onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing || loadingGroups || loadingPerms}
            title="Refresh groups & permissions"
            className="compare-btn"
          >
            <svg
              width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
              style={{ animation: refreshing ? "cug-spin 0.8s linear infinite" : "none" }}
            >
              <polyline points="23 4 23 10 17 10"/>
              <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
            {refreshing ? "Refreshing…" : "Refresh"}
          </button>
        </div>
      </div>

      <div className="cug-body" style={{ paddingTop: "28px", ...(activeTab === "visier-runbook" ? { maxWidth: "1600px" } : {}) }}>
        {activeTab === "create" && <CreateGroupTab permissions={permissions} loadingPerms={loadingPerms} />}
        {activeTab === "assign" && (
          <AssignPermissionsTab
            permissions={permissions} loadingPerms={loadingPerms}
            userGroups={userGroups}   loadingGroups={loadingGroups}
          />
        )}
        {activeTab === "unassign" && (
          <UnassignPermissionsTab
            permissions={permissions} loadingPerms={loadingPerms}
            userGroups={userGroups}   loadingGroups={loadingGroups}
          />
        )}
        {activeTab === "view" && (
          <ViewPermissionsTab
            permissions={permissions} loadingPerms={loadingPerms}
            userGroups={userGroups}   loadingGroups={loadingGroups}
          />
        )}
        {activeTab === "versioned-add-users" && <VersionedAddUsersTab />}
        {activeTab === "versioned-delete-users" && <VersionedDeleteUsersTab />}
        {activeTab === "nonversioned-add-users" && <NonVersionedAddUsersTab />}
        {activeTab === "nonversioned-delete-users" && <NonVersionedDeleteUsersTab />}
        {activeTab === "delete-group" && (
          <DeleteGroupTab
            userGroups={userGroups} loadingGroups={loadingGroups}
            onDeleted={handleRefresh}
          />
        )}
        {activeTab === "delete-permission" && (
          <DeletePermissionTab
            permissions={permissions} loadingPerms={loadingPerms}
            onDeleted={handleRefresh}
          />
        )}
        {activeTab === "population-access" && <PopulationAccessTab />}
        {activeTab === "visier-runbook" && <VisierRunbook canRebuild={isSuperAdmin} />}
      </div>

      <style>{`
        @keyframes cug-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        .tab-btn-danger { color: #ef4444 !important; }
        .tab-btn-danger:hover { background: rgba(239,68,68,0.08) !important; }
        .tab-btn-danger.active { border-bottom-color: #ef4444 !important; color: #ef4444 !important; }
        .vp-table { border: 1px solid var(--border); border-radius: var(--radius-md); overflow: hidden; }
        .vp-header { display: grid; grid-template-columns: 1fr 1fr; background: var(--bg-raised); border-bottom: 1px solid var(--border); }
        .vp-header .vp-col { font-family: var(--font-mono); font-size: 10px; text-transform: uppercase; letter-spacing: 0.06em; color: var(--text-muted); padding: 8px 14px; }
        .vp-row { display: grid; grid-template-columns: 1fr 1fr; border-bottom: 1px solid var(--border); transition: background 0.1s; }
        .vp-row:last-child { border-bottom: none; }
        .vp-row:hover { background: var(--bg-hover); }
        .vp-col { padding: 10px 14px; font-size: 13px; color: var(--text-secondary); display: flex; align-items: center; }
        .vp-col-id { font-family: var(--font-mono); font-size: 11px; color: var(--text-muted); }
      `}</style>
    </div>
  );
}
